import { FormEvent, useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useAuth } from "../../../context/AuthContext/AuthContext";
import api from "../../../api/axiosInstance";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Upload from "../components/Image/Upload";

const CreatePost = () => {
  interface CategoryDTO {
    id: string;
    categoryName: string;
  }

  const { isAuthenticated, isAdmin, userId } = useAuth();
  const navigate = useNavigate();
  // Values
  const [categories, setCategories] = useState<CategoryDTO[]>([]);
  const [coverImgUrl, setCoverImgUrl] = useState<any>("");
  const [title, setTitle] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [abstract, setAbstract] = useState<string>("");
  const [content, setContent] = useState<string>("");

  // Quill media
  const [img, setImg] = useState<any>("");
  const [video, setVideo] = useState<any>("");

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (newPost: any) => {
      return api.post(
        // `${process.env.REACT_APP_BACKEND_ROUTE}Blog/posts`,
        `Blog/posts`,
        newPost
      );
    },
    onSuccess: async (res) => {
      toast.success("პოსტი გამოქვეყნდა");
      await queryClient.removeQueries({ queryKey: ["posts"], exact: false });
      navigate(`/blog/posts/${res.data.data.id}`);
    },
  });

  const isQuillContentEmpty = (html: string): boolean => {
    const stripped = html
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return stripped === "";
  };

  const submitDisabled: boolean =
    mutation.isPending ||
    !title.trim() ||
    !abstract.trim() ||
    isQuillContentEmpty(content) ||
    !selectedCategory.trim();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data = {
      title: title,
      categoryId: selectedCategory,
      abstract: abstract,
      content: content,
      authorId: userId,
      coverImgUrl: coverImgUrl.filePath || "",
    };
    mutation.mutate(data);
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await api.get(
        // `${process.env.REACT_APP_BACKEND_ROUTE}Dictionary/Category`
        `Dictionary/Category`
      );
      setCategories(response.data);
    };

    fetchCategories();
  }, []);

  // Add image to quill
  useEffect(() => {
    img && setContent((prev) => prev + `<p><image src="${img.url}"/></p>`);
  }, [img]);

  // Add video to quill
  useEffect(() => {
    video &&
      setContent(
        (prev) => prev + `<p><iframe class="ql-video" src="${video.url}"/></p>`
      );
  });

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="flex items-center justify-center w-100">
        გთხოვთ გაიაროთ ავტორიზაცია
      </div>
    );
  }

  return (
    <div className="p-8 min-w-64 flex flex-col gap-6">
      <h1 className="text-xl font-light">შექმენი პოსტი</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 flex-1">
        <Upload type="image" setData={setCoverImgUrl}>
          <button
            type="button"
            className="w-max p-2 shadow-md rounded-xl text-sm text-gray-500 bg-white"
          >
            დაამატე გარეკანის სურათი
          </button>
        </Upload>

        <input
          name="title"
          className="text-4xl font-semibold bg-transparent outline-none"
          type="text"
          placeholder="სათაური"
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="flex items-center flex-wrap gap-4">
          <label htmlFor="category" className="text-sm">
            აირჩიე კატეგორია
          </label>
          <select
            name="categoryId"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-2 rounded-xl bg-white shadow-md text-sm"
          >
            <option value="" disabled>
              ...
            </option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.categoryName}
              </option>
            ))}
          </select>
        </div>
        <textarea
          className="p-4 rounded-xl bg-white shadow-md min-h-[100px]"
          name="abstract"
          id="post-description"
          placeholder="მოკლე აღწერა"
          onChange={(e) => setAbstract(e.target.value)}
        />
        <div className="flex flex-col">
          <div className="flex flex-row gap-2 mr-2">
            <Upload type="image" setData={setImg}>
              🖼️
            </Upload>
            <Upload type="video" setData={setVideo}>
              📹
            </Upload>
          </div>
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            className="flex-1 rounded-xl bg-white shadow-md min-h-[200px]"
          />
        </div>
        <button
          disabled={submitDisabled}
          className="bg-blue-800 text-white font-medium rounded-xl mt-4 p-2 w-36 disabled:bg-blue-200 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? "იტვირთება" : "დაპოსტე"}
        </button>
        {mutation.isError && <span>{mutation.error.message}</span>}
      </form>
    </div>
  );
};

export default CreatePost;
